import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatElapsed, GAME_PLAY_HINTS, GAME_RANKING_HINTS } from '@/utils/groupGameBareme';
import useGameTimer from '@/components/games/useGameTimer';
import SudokuBoard from '@/components/games/SudokuBoard';
import WordSearchBoard from '@/components/games/WordSearchBoard';
import DifferenceBoard from '@/components/games/DifferenceBoard';
import MemoryBoard from '@/components/games/MemoryBoard';
import PuzzleBoard from '@/components/games/PuzzleBoard';
import PuzzleIntroDemo from '@/components/games/PuzzleIntroDemo';
import MemoryIntroDemo from '@/components/games/MemoryIntroDemo';
import WordSearchIntroDemo from '@/components/games/WordSearchIntroDemo';
import SudokuIntroDemo from '@/components/games/SudokuIntroDemo';
import GameResultsPanel from '@/components/games/GameResultsPanel';

const RULES_OPEN_KEY = 'prysma_game_rules_open';

function readRulesOpen() {
  try {
    return sessionStorage.getItem(RULES_OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

function puzzleHasProgress(progress) {
  const slots = progress?.slots;
  if (Array.isArray(slots) && slots.some((v) => v != null)) return true;
  return false;
}

function memoryHasProgress(progress) {
  if ((Number(progress?.moves) || 0) > 0) return true;
  if (Array.isArray(progress?.matched) && progress.matched.length > 0) return true;
  return false;
}

function wordSearchHasProgress(progress) {
  if (Array.isArray(progress?.found) && progress.found.length > 0) return true;
  if (Array.isArray(progress?.foundCells) && progress.foundCells.length > 0) return true;
  return false;
}

function sudokuHasProgress(progress) {
  if ((Number(progress?.errors) || 0) > 0) return true;
  if (Array.isArray(progress?.errorCells) && progress.errorCells.length > 0) return true;
  return Array.isArray(progress?.board);
}

function needsGoGate(kind) {
  return kind === 'puzzle' || kind === 'memory' || kind === 'mots_meles' || kind === 'sudoku';
}

function hasGatedProgress(kind, progress) {
  if (kind === 'puzzle') return puzzleHasProgress(progress);
  if (kind === 'memory') return memoryHasProgress(progress);
  if (kind === 'mots_meles') return wordSearchHasProgress(progress);
  if (kind === 'sudoku') return sudokuHasProgress(progress);
  return false;
}

export function GameHintBlock({ kind }) {
  const play = GAME_PLAY_HINTS[kind];
  const rank = GAME_RANKING_HINTS[kind];
  const [open, setOpen] = useState(readRulesOpen);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  if (!play && !rank?.length) return null;

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { sessionStorage.setItem(RULES_OPEN_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="text-xs leading-relaxed text-gray-700 dark:text-gray-200 bg-orange-50/80 dark:bg-orange-950/30 border border-orange-200/80 dark:border-orange-900/50 rounded-md px-3 py-2 mb-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 w-full text-left font-semibold text-gray-800 dark:text-gray-100"
      >
        {open ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
        Règles
      </button>
      {open ? (
        <div className="mt-1.5">
          {play ? <p>{play}</p> : null}
          {rank?.length ? (
            <div className={play ? 'mt-2' : ''}>
              <p className="font-semibold text-gray-800 dark:text-gray-100">Classement</p>
              <ol className="list-decimal list-inside mt-0.5 space-y-0.5">
                {rank.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function GamePlayPanel({
  instanceId,
  token,
  meId,
  phase,
  showHint = true,
  onFinished,
  previewKind,
  previewPayload,
}) {
  const isPreview = Boolean(previewPayload);
  const timer = useGameTimer({ instanceId: isPreview ? undefined : instanceId, token: isPreview ? undefined : token });
  const [result, setResult] = useState(null);
  const [started, setStarted] = useState(false);
  const [previewProgress, setPreviewProgress] = useState({});
  const [previewElapsed, setPreviewElapsed] = useState(0);
  const [previewClockOn, setPreviewClockOn] = useState(false);
  const [demoKind, setDemoKind] = useState(null); // 'puzzle' | 'memory' | 'mots_meles' | 'sudoku' | null
  const [frozenDemoMs, setFrozenDemoMs] = useState(0);
  const [playStarted, setPlayStarted] = useState(false);
  const previewBaseRef = useRef(0);

  useEffect(() => {
    if (!isPreview) return undefined;
    setResult(null);
    setPreviewProgress({});
    setPreviewElapsed(0);
    previewBaseRef.current = 0;
    setStarted(true);
    setDemoKind(null);
    setFrozenDemoMs(0);
    const gated = needsGoGate(previewKind);
    setPlayStarted(!gated);
    setPreviewClockOn(!gated);
  }, [isPreview, previewPayload, previewKind]);

  useEffect(() => {
    if (!isPreview || !previewClockOn) return undefined;
    const base = previewBaseRef.current;
    const startedAt = Date.now();
    const id = window.setInterval(() => setPreviewElapsed(base + (Date.now() - startedAt)), 200);
    return () => window.clearInterval(id);
  }, [isPreview, previewClockOn, previewPayload]);

  useEffect(() => {
    if (isPreview || phase === 'results') return undefined;
    let cancelled = false;
    (async () => {
      const data = await timer.start();
      if (cancelled) return;
      setStarted(true);
      if (data?.alreadyWon) {
        setResult({ alreadyWon: true });
        return;
      }
      const kindNow = (data?.kind || '').trim();
      if (needsGoGate(kindNow)) {
        const elapsed = Number(data?.elapsed_ms) || 0;
        const resume = elapsed > 0 || hasGatedProgress(kindNow, data?.progress);
        if (resume) {
          setPlayStarted(true);
        } else {
          await timer.pause();
          if (cancelled) return;
          setFrozenDemoMs(elapsed);
          setPlayStarted(false);
        }
      } else {
        setPlayStarted(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPreview, instanceId, phase, timer.start, timer.pause]);

  const startGatedPlay = async () => {
    if (playStarted) return;
    setPlayStarted(true);
    if (isPreview) {
      previewBaseRef.current = 0;
      setPreviewElapsed(0);
      setPreviewClockOn(true);
      return;
    }
    // Relancer un segment côté serveur (pause initiale / Go) — startTick seul ne comptait pas le temps.
    await timer.start();
  };

  const openGameDemo = async (kindDemo) => {
    if (isPreview) {
      previewBaseRef.current = previewElapsed;
      setFrozenDemoMs(previewElapsed);
      setPreviewClockOn(false);
      setDemoKind(kindDemo);
      return;
    }
    const running = needsGoGate(kindDemo) ? playStarted : true;
    if (running) {
      setFrozenDemoMs(timer.elapsedMs || 0);
      await timer.pause();
    } else {
      setFrozenDemoMs(timer.elapsedMs || frozenDemoMs || 0);
    }
    setDemoKind(kindDemo);
  };

  const closeGameDemo = async () => {
    const was = demoKind;
    setDemoKind(null);
    if (isPreview) {
      previewBaseRef.current = frozenDemoMs;
      setPreviewElapsed(frozenDemoMs);
      if (!needsGoGate(was) || playStarted) setPreviewClockOn(true);
      return;
    }
    if (needsGoGate(was) && !playStarted) return;
    // Reprendre le chrono serveur après la pause Démo
    await timer.start();
  };

  const onSolved = async () => {
    if (isPreview) {
      setResult({ ok: true, preview: true });
      return;
    }
    const data = await timer.complete();
    setResult(data || { ok: true });
  };

  const finished = !!(result?.ok || result?.alreadyWon);
  const payload = isPreview ? previewPayload : timer.payload;
  const kind = isPreview ? previewKind : timer.kind;
  const progress = isPreview ? previewProgress : timer.progress;
  const elapsedMs = demoKind || (needsGoGate(kind) && !playStarted)
    ? (isPreview ? frozenDemoMs : (frozenDemoMs || timer.elapsedMs || 0))
    : (isPreview ? previewElapsed : timer.elapsedMs);
  const saveProgress = isPreview
    ? (next) => setPreviewProgress(next || {})
    : (next) => timer.saveProgress(next);

  useEffect(() => {
    if (isPreview || !finished) return;
    onFinished?.();
  }, [isPreview, finished, onFinished]);

  if (!isPreview && onFinished && finished) return null;

  if (!isPreview && !onFinished && (phase === 'results' || (result?.alreadyWon && !timer.payload))) {
    return <GameResultsPanel instanceId={instanceId} token={token} meId={meId} />;
  }

  const board = payload && (
    kind === 'sudoku' ? (
      <SudokuBoard
        payload={payload}
        savedBoard={progress?.board}
        savedErrors={progress?.errors}
        savedErrorCells={progress?.errorCells}
        onSolved={onSolved}
        onProgress={saveProgress}
        playStarted={playStarted}
        onGo={startGatedPlay}
        onDemoClick={!finished ? () => openGameDemo('sudoku') : undefined}
      />
    )
    : kind === 'mots_meles' ? (
      <WordSearchBoard
        payload={payload}
        savedFound={progress?.found}
        savedFoundCells={progress?.foundCells}
        savedBonusGuess={progress?.bonusGuess}
        savedBonusSolved={progress?.bonusSolved}
        onSolved={onSolved}
        onProgress={saveProgress}
        playStarted={playStarted}
        onGo={startGatedPlay}
        onDemoClick={!finished ? () => openGameDemo('mots_meles') : undefined}
      />
    )
    : kind === 'memory' ? (
      <MemoryBoard
        payload={payload}
        savedMatched={progress?.matched}
        savedMoves={progress?.moves}
        compact={finished}
        onSolved={onSolved}
        onProgress={saveProgress}
        playStarted={playStarted}
        onGo={startGatedPlay}
        onDemoClick={!finished ? () => openGameDemo('memory') : undefined}
      />
    )
    : kind === 'difference' ? (
      <DifferenceBoard
        payload={payload}
        savedFound={progress?.found}
        onSolved={onSolved}
        onProgress={saveProgress}
      />
    )
    : kind === 'puzzle' ? (
      <PuzzleBoard
        payload={payload}
        savedSlots={progress?.slots}
        savedTray={progress?.tray}
        savedTrayPos={progress?.trayPos}
        onSolved={onSolved}
        onProgress={saveProgress}
        playStarted={playStarted}
        onGo={startGatedPlay}
        onDemoClick={!finished ? () => openGameDemo('puzzle') : undefined}
      />
    )
    : null
  );

  if (demoKind === 'puzzle') {
    return (
      <div className="mt-3 min-h-0">
        {showHint ? <GameHintBlock kind="puzzle" /> : null}
        <PuzzleIntroDemo
          elapsedLabel={formatElapsed(elapsedMs)}
          onClose={closeGameDemo}
        />
      </div>
    );
  }

  if (demoKind === 'memory') {
    return (
      <div className="mt-3 min-h-0">
        {showHint ? <GameHintBlock kind="memory" /> : null}
        <MemoryIntroDemo
          elapsedLabel={formatElapsed(elapsedMs)}
          onClose={closeGameDemo}
        />
      </div>
    );
  }

  if (demoKind === 'mots_meles') {
    return (
      <div className="mt-3 min-h-0">
        {showHint ? <GameHintBlock kind="mots_meles" /> : null}
        <WordSearchIntroDemo
          elapsedLabel={formatElapsed(elapsedMs)}
          onClose={closeGameDemo}
        />
      </div>
    );
  }

  if (demoKind === 'sudoku') {
    return (
      <div className="mt-3 min-h-0">
        {showHint ? <GameHintBlock kind="sudoku" /> : null}
        <SudokuIntroDemo
          elapsedLabel={formatElapsed(elapsedMs)}
          onClose={closeGameDemo}
        />
      </div>
    );
  }

  return (
    <div className="mt-3 min-h-0">
      {showHint ? <GameHintBlock kind={kind} /> : null}
      <div className={kind === 'memory' ? 'mb-1.5' : 'mb-3'}>
        <p className="text-sm font-mono font-bold text-[#f76f4b]">{formatElapsed(elapsedMs)}</p>
        {kind === 'memory' && (
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mt-0.5">
            {Number(progress?.moves) || 0} coup{(Number(progress?.moves) || 0) !== 1 ? 's' : ''}
          </p>
        )}
        {timer.error && <p className="text-xs text-red-500 mt-1">{timer.error}</p>}
        {result?.ok && (
          <p className="text-xs text-green-600 mt-1">
            {result.preview
              ? 'Test terminé. Cette partie n’est pas enregistrée au classement.'
              : `Bravo, partie validée !${result?.rank != null ? ` Rang ${result.rank} · ${result.points || 0} pts` : ''}`}
          </p>
        )}
      </div>
      <div className="w-full min-w-0 overflow-visible">{started ? board : <p className="text-xs text-gray-400">Chargement…</p>}</div>
      {!isPreview && finished && !onFinished ? (
        <GameResultsPanel instanceId={instanceId} token={token} meId={meId} compact />
      ) : null}
    </div>
  );
}
